/**
 * The shape of a telc listening recording, measured.
 *
 * A listening part is one track that runs once: an announcer opens it, reads
 * the instruction printed on the paper, sounds a tone, numbers an item, leaves
 * time to read the question, plays the recording, and leaves time to answer.
 * None of that is written down anywhere. telc publishes the paper and the
 * recording; the paper prints the *recordings'* words, not one of the
 * announcer's and not a single pause length.
 *
 * So every number below was measured off the published mock exam by
 * `scripts/telc-audio/map-track.mjs`, which times the track with ffmpeg rather
 * than asking a model, and cross-checks what it timed against what Whisper
 * heard. `scripts/telc-audio/README.md` is the method and the raw findings.
 *
 * This is the only place the shape is written down. The generator renders from
 * it, and the ranges the measurement found are collapsed to one value here on
 * purpose: a slot that varies between 10.06 s and 10.56 s across eighteen items
 * is a studio hitting ten seconds by hand, not a rule with a spread in it.
 *
 * What is *not* here is anything the announcer says beyond the furniture. The
 * instruction is read out of the set's own `instruction` field, the way the
 * announcer reads what is printed in front of the candidate — one copy of those
 * words instead of two that can drift apart.
 */
import { LISTENING_READ_SECONDS } from "./structure.ts";

/**
 * The tone between items: a C major triad, 1.5 s long.
 *
 * Measured at 262, 393 and 660 Hz with the middle partial loudest — which is
 * also why the whole tone repeats at 131 Hz, the frequency all three divide
 * into and the one its autocorrelation finds. It is synthesised from these
 * three numbers rather than sampled: the shape of the track is a fact about how
 * the exam is administered, but telc's recording is telc's.
 */
export const LISTENING_TONE = {
  seconds: 1.5,
  /** Relative level in dB, loudest partial at 0. */
  partials: [
    { hz: 262, db: -14 },
    { hz: 393, db: 0 },
    { hz: 660, db: -5 },
  ],
  /** Long enough that neither end clicks, short enough to still be a chime. */
  fadeSeconds: 0.05,
} as const;

/**
 * The answering-machine beep telc puts in front of some voicemail messages —
 * a plain 1.4 kHz tone, a second long. Part 1 only, and not on every message.
 */
export const LISTENING_BEEP = {
  seconds: 1,
  partials: [{ hz: 1407, db: 0 }],
  fadeSeconds: 0.02,
} as const;

export const LISTENING_TRACK = {
  /** Before the announcer starts, and after the track's own title. */
  leadIn: 0.5,
  afterTitle: 1.2,
  /** After "Rozumienie ze słuchu, część pierwsza", before the instruction. */
  afterHeader: 0.9,
  /** Between two sentences of one announcement. */
  betweenSentences: 0.7,
  /**
   * Between two turns of one dialogue.
   *
   * The median of the 95 gaps measured inside telc's recordings. Without it the
   * turns butt straight against each other: the recording stops sounding like a
   * conversation, and it finishes a fifth early, which is a fifth less
   * listening than the paper asks for.
   */
  betweenTurns: 0.5,
  /** After the instruction, before the first item is numbered. */
  afterInstruction: 1.5,

  /** After the tone, before the announcer numbers the item. */
  afterTone: 2,
  /**
   * Between the number and the recording.
   *
   * `read` is where the candidate reads the question they are about to answer —
   * parts 1 and 2, which number every item. `cued` is parts 3 and 4, which give
   * the reading time up front in one block and leave only a beat here.
   */
  afterLabel: { read: 10.2, cued: 2.2 },
  /** After the recording: the only time a candidate gets to write anything. */
  answer: 10.1,
  /**
   * After the block of reading time in parts 3 and 4, the tone sounds and then
   * the recording starts — a longer beat than anywhere else on the track.
   */
  afterBlockRead: 10.8,

  /** Around "Koniec pierwszej części rozumienia ze słuchu". */
  beforeClose: 2,
  afterClose: 5.5,
  /** Around "Koniec rozumienia ze słuchu" and "Dziękujemy za uwagę". */
  beforeEnd: 3.3,
  tail: 5.2,

  /** Read the questions before the recording: a minute, or half of one. */
  blockRead: LISTENING_READ_SECONDS,
} as const;

/**
 * How much a recording says, in words, on telc's own paper.
 *
 * **Words, not seconds — because words are what telc holds steady.** Its five
 * part-2 dialogues run 142, 144, 145, 145 and 146 words; they last 55 to 64
 * seconds, because actors are people and some of them talk faster. The quantity
 * the paper fixes is how much language the candidate has to process, and the
 * duration is what falls out of it.
 *
 * Writing to a duration instead inverts that, and quietly. A script written 10%
 * long and read 30% fast lands on the right number of seconds and is harder
 * than the exam: more words per minute, less time to hold each one. Every
 * measurement here is the printed transcript reassembled column by column from
 * the paper, checked against a whole-section word count that knows nothing
 * about the columns — 2215 against 2212.
 *
 * The ranges are what telc's own five recordings of a kind actually spread
 * across, examples included — so they are a sample, not a rule, and
 * `LISTENING_LENGTH_TOLERANCE` widens them by a twentieth before anything is
 * reported. A recording five words under telc's shortest dialogue is not a
 * finding; one at half the length is, and that is the difference the report
 * exists to show.
 */
export const LISTENING_LENGTHS = {
  messages: [66, 78],
  dialogs: [142, 146],
  interview: [734, 734],
  opinions: [72, 91],
} as const satisfies Record<string, readonly [number, number]>;

/** How far outside the observed spread a recording may sit before it is worth saying. */
export const LISTENING_LENGTH_TOLERANCE = 0.05;

/**
 * How fast telc's track is read — two numbers, because it is two jobs.
 *
 * The announcer reads at 133 words a minute and the actors at 147: about a
 * tenth apart, and audibly so. The announcer is giving instructions somebody
 * has to follow with a pencil in their hand; the recordings are people talking
 * to each other. Using one figure for both makes the furniture sound hurried or
 * the conversations sound dictated.
 *
 * Measured off `uebungstest-1`: 2101 printed words over the 895 seconds the
 * recordings occupy, and the announcer's instructions against the same
 * instructions printed on the task pages. Individual recordings run anywhere
 * from 122 to 175 wpm — they are read by people — but every part averages
 * between 137 and 145.
 *
 * The word counts come from the printed transcript, never from the
 * transcriber: Whisper drops about a word in nine, which on its own would have
 * put the recordings at 131 wpm and every target ten per cent out.
 */
export const LISTENING_TEMPO = {
  announcer: 133,
  recording: 141,
} as const;

/**
 * The furniture, in Polish.
 *
 * Functional labels, and deliberately not telc's sentences: an announcement
 * naming telc on a paper telc did not write is the same mistake the Writing
 * card is not allowed to make. The set's own title opens the track.
 */
export const LISTENING_WORDS = {
  /** "Część pierwsza", by part index. */
  part: ["Część pierwsza.", "Część druga.", "Część trzecia.", "Część czwarta."],
  example: "Przykład.",
  /** "Koniec pierwszej części rozumienia ze słuchu." */
  close: [
    "Koniec pierwszej części rozumienia ze słuchu.",
    "Koniec drugiej części rozumienia ze słuchu.",
    "Koniec trzeciej części rozumienia ze słuchu.",
    "Koniec czwartej części rozumienia ze słuchu.",
  ],
  end: "Koniec rozumienia ze słuchu.",
  thanks: "Dziękujemy za uwagę.",
} as const;

/** One to twenty-three in words, so the announcer never reads a numeral. */
const NUMERALS = [
  "", "jeden", "dwa", "trzy", "cztery", "pięć", "sześć", "siedem", "osiem",
  "dziewięć", "dziesięć", "jedenaście", "dwanaście", "trzynaście", "czternaście",
  "piętnaście", "szesnaście", "siedemnaście", "osiemnaście", "dziewiętnaście",
  "dwadzieścia", "dwadzieścia jeden", "dwadzieścia dwa", "dwadzieścia trzy",
];

/**
 * How the announcer names the set: "Zestaw ćwiczeniowy czternaście".
 *
 * The title is written for the screen — "telc B1·B2 — zestaw ćwiczeniowy 14" —
 * and a synthesiser may not be handed a digit, so the track says the number in
 * words and leaves the exam's name to the part header that follows.
 */
export function spokenSetTitle(title: string): string {
  const n = Number(/(\d+)\s*$/u.exec(title)?.[1]);
  const word = NUMERALS[n];
  if (!word) throw new Error(`"${title}" ends in no number the announcer can say`);
  return `Zestaw ćwiczeniowy ${word}`;
}

/**
 * How the announcer numbers one recording: "Numer pięć i sześć".
 *
 * The numbers come from the set's own items rather than from a count kept here,
 * because a dialogue carries two of them and a message one, and which is which
 * is already decided by the walk in `items.ts`. Words, not numerals: a
 * synthesiser reads "11" as often wrongly as rightly, and the exam says them
 * aloud either way.
 */
export function listeningLabel(numbers: number[]): string {
  const words = numbers.map((n) => {
    const word = NUMERALS[n];
    if (!word) throw new Error(`item ${n} has no spoken form — add it to NUMERALS rather than read a numeral`);
    return word;
  });
  if (words.length === 0) throw new Error("an item with no number cannot be announced");
  if (words.length > 2) throw new Error(`${numbers.join(", ")}: no recording carries three items`);
  return `Numer ${words.join(" i ")}.`;
}
